import { useEffect, useState, useCallback } from "react";
import { Loader2, PackagePlus, Plus } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import InventorySubNav from "@/components/inventory/InventorySubNav";
import SelectWithInlineAdd from "@/components/inventory/SelectWithInlineAdd";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

const emptyNewItem = { name: "", generic_name: "", company_name: "", category_id: "", form_id: "", unit_id: "", reorder_level: "0" };

export default function ReceiveStock() {
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [forms, setForms] = useState([]);
  const [units, setUnits] = useState([]);
  const [items, setItems] = useState([]);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newItem, setNewItem] = useState(emptyNewItem);
  const [form, setForm] = useState({ item_id: "", location_id: "", expiry_month: "", expiry_year: "", quantity_received: "", batch_number: "", unit_cost: "" });
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const refreshReferenceData = useCallback(() => {
    api.get("/inventory/categories").then(({ data }) => setCategories(data));
    api.get("/inventory/forms").then(({ data }) => setForms(data));
    api.get("/inventory/units").then(({ data }) => setUnits(data));
  }, []);

  useEffect(() => {
    api.get("/inventory/locations").then(({ data }) => setLocations(data));
    refreshReferenceData();
    api.get("/inventory/items", { params: { include_archived: false } }).then(({ data }) => setItems(data));
  }, [refreshReferenceData]);

  const refreshItems = useCallback(async () => {
    const { data } = await api.get("/inventory/items");
    setItems(data);
  }, []);

  const handleCreateCategory = async (name) => {
    const { data } = await api.post("/inventory/categories", { name });
    refreshReferenceData();
    return data;
  };
  const handleCreateForm = async (name) => {
    const { data } = await api.post("/inventory/forms", { name });
    refreshReferenceData();
    return data;
  };
  const handleCreateUnit = async (name) => {
    const { data } = await api.post("/inventory/units", { name });
    refreshReferenceData();
    return data;
  };

  const handleCreateItem = async () => {
    if (!newItem.name.trim() || !newItem.category_id || !newItem.form_id || !newItem.unit_id || !form.location_id) {
      toast({ variant: "destructive", title: "Pick a drawer, category, form and unit first, and enter a product name" });
      return;
    }
    try {
      const { data } = await api.post("/inventory/items", { ...newItem, reorder_level: Number(newItem.reorder_level) || 0, location_id: form.location_id });
      toast({ title: "Item created" });
      setForm((f) => ({ ...f, item_id: data.id }));
      setCreatingNew(false);
      setNewItem(emptyNewItem);
      refreshItems();
    } catch (err) {
      toast({ variant: "destructive", title: "Could not create item", description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const handleSubmit = async () => {
    if (!form.item_id || !form.location_id || !form.expiry_month || !form.expiry_year || !form.quantity_received) {
      toast({ variant: "destructive", title: "Item, drawer, expiry and quantity are required" });
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inventory/batches", {
        item_id: form.item_id,
        location_id: form.location_id,
        expiry_month: Number(form.expiry_month),
        expiry_year: Number(form.expiry_year),
        quantity_received: Number(form.quantity_received),
        batch_number: form.batch_number || null,
        unit_cost: form.unit_cost ? Number(form.unit_cost) : null,
      });
      toast({ title: "Stock received" });
      setForm({ item_id: "", location_id: form.location_id, expiry_month: "", expiry_year: "", quantity_received: "", batch_number: "", unit_cost: "" });
    } catch (err) {
      toast({ variant: "destructive", title: "Could not receive stock", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-amtm-bg">
      <StaffHeader />
      <div className="max-w-2xl mx-auto px-4 py-6 pb-24">
        <h1 className="font-display text-3xl text-amtm-text mb-2" data-testid="receive-stock-title">Receive Stock</h1>
        <InventorySubNav />

        <div className="bg-amtm-surface border border-amtm-border rounded-xl p-5 space-y-4">
          <div>
            <Label>Drawer</Label>
            <Select value={form.location_id} onValueChange={(v) => setForm((f) => ({ ...f, location_id: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="receive-stock-location-select"><SelectValue placeholder="Select drawer" /></SelectTrigger>
              <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {!creatingNew ? (
            <div>
              <div className="flex items-center justify-between">
                <Label>Item</Label>
                <button type="button" onClick={() => setCreatingNew(true)} className="text-xs text-amtm-teal font-medium flex items-center gap-1" data-testid="receive-stock-new-item-toggle">
                  <Plus className="h-3 w-3" /> New item
                </button>
              </div>
              <Select value={form.item_id} onValueChange={(v) => setForm((f) => ({ ...f, item_id: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="receive-stock-item-select"><SelectValue placeholder="Select existing item" /></SelectTrigger>
                <SelectContent>
                  {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}{i.company_name ? ` — ${i.company_name}` : ""} ({i.location_name})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="border border-amtm-border rounded-lg p-4 space-y-3 bg-amtm-bg" data-testid="receive-stock-new-item-form">
              <div className="flex items-center justify-between">
                <p className="font-medium text-sm text-amtm-text">New item</p>
                <button type="button" onClick={() => setCreatingNew(false)} className="text-xs text-amtm-textMuted" data-testid="receive-stock-new-item-cancel">Cancel</button>
              </div>
              <Input placeholder="Product name" value={newItem.name} onChange={(e) => setNewItem((v) => ({ ...v, name: e.target.value }))} data-testid="new-item-name-input" />
              <Input placeholder="Generic name (optional)" value={newItem.generic_name} onChange={(e) => setNewItem((v) => ({ ...v, generic_name: e.target.value }))} data-testid="new-item-generic-input" />
              <Input placeholder="Company name (optional)" value={newItem.company_name} onChange={(e) => setNewItem((v) => ({ ...v, company_name: e.target.value }))} data-testid="new-item-company-input" />
              <SelectWithInlineAdd
                value={newItem.category_id}
                onChange={(v) => setNewItem((it) => ({ ...it, category_id: v }))}
                options={categories}
                onCreate={handleCreateCategory}
                placeholder="Category"
                addLabel="Add new category"
                testId="new-item-category-select"
              />
              <div className="grid grid-cols-2 gap-2">
                <SelectWithInlineAdd
                  value={newItem.form_id}
                  onChange={(v) => setNewItem((it) => ({ ...it, form_id: v }))}
                  options={forms}
                  onCreate={handleCreateForm}
                  placeholder="Form"
                  addLabel="Add new form"
                  testId="new-item-form-select"
                />
                <SelectWithInlineAdd
                  value={newItem.unit_id}
                  onChange={(v) => setNewItem((it) => ({ ...it, unit_id: v }))}
                  options={units}
                  onCreate={handleCreateUnit}
                  placeholder="Unit"
                  addLabel="Add new unit"
                  testId="new-item-unit-select"
                />
              </div>
              <Input type="number" placeholder="Reorder level" value={newItem.reorder_level} onChange={(e) => setNewItem((v) => ({ ...v, reorder_level: e.target.value }))} data-testid="new-item-reorder-input" />
              <Button type="button" onClick={handleCreateItem} data-testid="new-item-save-button" className="bg-amtm-teal text-white hover:bg-amtm-tealHover">Create Item</Button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Expiry month</Label>
              <Input type="number" min="1" max="12" placeholder="MM" value={form.expiry_month} onChange={(e) => setForm((f) => ({ ...f, expiry_month: e.target.value }))} className="mt-1.5" data-testid="receive-stock-expiry-month-input" />
            </div>
            <div>
              <Label>Expiry year</Label>
              <Input type="number" placeholder="YYYY" value={form.expiry_year} onChange={(e) => setForm((f) => ({ ...f, expiry_year: e.target.value }))} className="mt-1.5" data-testid="receive-stock-expiry-year-input" />
            </div>
          </div>
          <div>
            <Label>Quantity received</Label>
            <Input type="number" min="0" step="any" value={form.quantity_received} onChange={(e) => setForm((f) => ({ ...f, quantity_received: e.target.value }))} className="mt-1.5" data-testid="receive-stock-quantity-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Batch number (optional)</Label>
              <Input value={form.batch_number} onChange={(e) => setForm((f) => ({ ...f, batch_number: e.target.value }))} className="mt-1.5" data-testid="receive-stock-batch-number-input" />
            </div>
            <div>
              <Label>Unit cost (optional)</Label>
              <Input type="number" step="any" value={form.unit_cost} onChange={(e) => setForm((f) => ({ ...f, unit_cost: e.target.value }))} className="mt-1.5" data-testid="receive-stock-unit-cost-input" />
            </div>
          </div>

          <Button onClick={handleSubmit} disabled={submitting} data-testid="receive-stock-submit-button" className="bg-amtm-teal text-white hover:bg-amtm-tealHover">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PackagePlus className="h-4 w-4 mr-2" />}
            Receive Stock
          </Button>
        </div>
      </div>
    </div>
  );
}
